import React, { useRef, useMemo } from 'react'; // Removed useState, useEffect
import { useNavigate } from 'react-router-dom';
// import logo from './wysupp-icon-green-transparent.svg'; // Removed - Handled by Footer/Header
// import AccountInfo from './AccountInfo'; // Removed - Handled by Header
import Header from './Header'; // Kept Header import
import { useAuth } from './AuthContext'; // Kept useAuth import
import { FaCalendarAlt, FaFileAlt, FaFileUpload, FaChartPie, FaChartLine, FaChartBar, FaEnvelopeOpenText, FaFacebook, FaTwitter, FaGithub, FaDribbble, FaPenFancy, FaPenAlt, FaPencilAlt, FaPencilRuler, FaUser, FaRProject, FaProjectDiagram } from 'react-icons/fa';
import logo from './wysupp-icon-green-transparent.svg'; // Keep logo for Footer

// Footer remains the same
const Footer = () => (
    <footer className="dashboard-footer">
        <div className="footer-logo">
            <img src={logo} alt="Wysupp Logo" />
            <span>Wysupp Validate</span>
        </div>
        <p className="footer-tagline">
            Streamlining Data Quality Management with powerful validation tools and insightful analytics.
        </p>
        <div className="footer-social-icons">
            <a href="#" aria-label="Facebook"><FaFacebook /></a>
            <a href="#" aria-label="Twitter"><FaTwitter /></a>
            <a href="#" aria-label="GitHub"><FaGithub /></a>
            <a href="#" aria-label="Dribbble"><FaDribbble /></a>
        </div>
        <p className="footer-copyright">
            © {new Date().getFullYear()} Wysupp. All rights reserved.
        </p>
    </footer>
);

export default function ProtectedPageCustomer() {
    // --- UPDATED --- Get user and selectedProjectName from context
    const { user, selectedProjectName } = useAuth(); // selectedProjectName might not be needed here, but keeping it consistent
    const navigate = useNavigate();
    const navigationRef = useRef(null);

    // --- REMOVED --- Local project state and effect removed
    // const [userProjects, setUserProjects] = useState([]);
    // const [selectedProjectName, setSelectedProjectName] = useState('');
    // useEffect(() => { ... });

    // --- REMOVED --- Local project change handler removed
    // const handleProjectChangeInHeader = (e) => { ... };

    // --- DERIVE userProjects from context ---
    const userProjects = user?.projects || []; // Needed for Header rendering logic (though Header gets it itself)

    // Helper function for navigation (Keep existing)
    const goTo = (path) => () => navigate(path);

    const handleScrollToNav = () => { // Keep existing
        navigationRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Nav Cards Logic (Keep existing)
    const { mainNavCards, adminNavCards } = useMemo(() => {
        const allCards = [
            {
                title: "Deadline Schedule",
                description: "Monitor and manage all validation deadlines with comprehensive scheduling tools and automated reminders.",
                icon: <FaCalendarAlt />,
                buttons: [
                    { text: "Access Schedule", type: "primary", onClick: goTo('/deadline-schedule') }
                ]
            },
            {
                title: "Interface Data Requisites",
                description: "Define and manage data requirements for all interface validations with detailed specification tools.",
                icon: <FaFileAlt />,
                buttons: [
                    { text: "Check Requisites", type: "primary", onClick: goTo('/interface-definitions') }
                ]
            },
            {
                title: "Interface Upload & Technical Validation",
                description: "Securely upload and process interface files with advanced validation and error checking capabilities.",
                icon: <FaFileUpload />,
                buttons: [
                    { text: "Upload Files", type: "primary", onClick: goTo('/interface-upload') }
                ]
            },
            {
                title: "Interface Validation Status & Discrepancy Control",
                description: "Track validation progress, monitor delivery status, and manage quality control processes with real-time updates and comprehensive reporting.",
                icon: <FaChartLine />,
                buttons: [
                    { text: "View Status Dashboard", type: "primary", onClick: goTo('/interface-status') }
                ]
            },
            {
                title: "General KPIs Dashboard",
                description: "Comprehensive analytics and key performance indicators for all validation processes and system metrics.",
                icon: <FaChartBar />,
                buttons: [
                    { text: "View Dashboard", type: "primary", onClick: goTo('/kpi-dashboard') }
                ]
            },
            {
                title: "Interface Status Management",
                description: "Oversee and manage the current tasks and statuses of all interface validations in one centralized location.",
                icon: <FaPencilRuler />,
                buttons: [
                    { text: "Manage Interfaces", type: "primary", onClick: goTo('/interface-management') }
                ]
            },
            {
                title: "User Management",
                description: "Create, update, and manage user accounts and roles with secure access controls and permissions.",
                icon: <FaUser />,
                role: "admin",
                buttons: [
                    { text: "Manage Users", type: "primary", onClick: goTo('/admin/manage-users') }
                ]
            },
            {
                title: "Project Management",
                description: "Create, edit, and oversee projects with detailed customization and user assignment features.",
                icon: <FaProjectDiagram />,
                role: "admin",
                buttons: [
                    { text: "Manage Projects", type: "primary", onClick: goTo('/admin/manage-projects') }
                ]
            },
            {
                title: "Log Monitoring",
                description: "Monitor system logs and user activities with detailed tracking and reporting features.",
                icon: <FaChartPie />,
                role: "admin",
                buttons: [
                    { text: "Monitor Logs", type: "primary", onClick: goTo('/admin/monitoring') }
                ]
            },
        ];

        const mainCards = allCards.filter(card => !card.role);
        const adminCards = allCards.filter(card => card.role === 'admin');

        if (user?.role !== 'admin' && user?.role !== 'manager') {
            return { mainNavCards: mainCards, adminNavCards: [] };
        }

        return { mainNavCards: mainCards, adminNavCards: adminCards };

    }, [user, navigate]);

    return (
        <div className="dashboard-container">
            {/* --- Header Section --- */}
            {/* --- UPDATED --- Use Header component with no props */}
            <Header />
            {/* --- END UPDATED --- */}

            {/* --- Hero Section (Keep existing) --- */}
            <section className="hero-section">
                <div className="hero-content">
                    <h1 className="hero-title">Welcome, {user ? user.username : ''}</h1>
                    <p className="hero-description">
                        Streamline your data validation processes with our comprehensive suite of tools.
                        Monitor deadlines, manage interfaces, and track KPIs all in one centralized
                        platform.
                    </p>
                    <div className="hero-buttons">
                        <button onClick={handleScrollToNav} className="hero-button primary">Get Started</button>
                    </div>
                </div>
            </section>

            {/* --- Main Navigation Section (Keep existing) --- */}
            <section className="main-navigation-section">
                <h2 className="section-title">Main Navigation</h2>
                <p className="section-subtitle" ref={navigationRef}>Choose your destination to manage validation processes efficiently</p>
                <div className="nav-cards-grid">
                    {mainNavCards.map((card, index) => (
                        <div key={index} className="nav-card">
                            <div className="card-icon-wrapper">
                                {card.icon}
                            </div>
                            <h3 className="card-title">{card.title}</h3>
                            <p className="card-description">{card.description}</p>
                            <div className="card-buttons">
                                {card.buttons.map((button, btnIndex) => (
                                    <button
                                        key={btnIndex}
                                        onClick={button.onClick}
                                        className={`card-button ${button.type === 'primary' ? 'primary' : 'secondary'}`}
                                    >
                                        {button.text}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* --- Admin Navigation Section (Keep existing) --- */}
            {adminNavCards.length > 0 && (
                <section className="main-navigation-section admin-section">
                   <h2 className="section-title">Admin Management</h2>
                   <p className="section-subtitle">Oversee and configure system-wide settings</p>
                   <div className="nav-cards-grid">
                       {adminNavCards.map((card, index) => (
                           <div key={index} className="nav-card">
                               <div className="card-icon-wrapper">
                                   {card.icon}
                               </div>
                               <h3 className="card-title">{card.title}</h3>
                               <p className="card-description">{card.description}</p>
                               <div className="card-buttons">
                                   {card.buttons.map((button, btnIndex) => (
                                       <button
                                           key={btnIndex}
                                           onClick={button.onClick}
                                           className={`card-button ${button.type === 'primary' ? 'primary' : 'secondary'}`}
                                       >
                                           {button.text}
                                       </button>
                                   ))}
                               </div>
                           </div>
                       ))}
                   </div>
                </section>
            )}

            {/* --- Footer (Keep existing) --- */}
            <Footer />

            {/* --- Inline Styles (Remove old header styles if they were here) --- */}
            <style>{`
                :root {
                    --dark-bg: #1a202c;
                    --card-bg: #2d3748;
                    --text-color: #e2e8f0;
                    --light-text: #a0aec0;
                    --green-accent: #48bb78;
                    --primary-button-bg: #48bb78;
                    --primary-button-hover: #38a169;
                    --secondary-button-bg: #4a5568;
                    --secondary-button-hover: #2d3748;
                    --border-color: #4a5568;
                }

                .dashboard-container {
                    background-color: var(--dark-bg);
                    min-height: 100vh;
                    color: var(--text-color);
                    font-family: 'Inter', sans-serif;
                }

                /* Header styles are now handled by the Header component */

                /* Hero Section Styles */
                .hero-section {
                    background: linear-gradient(135deg, #1f2937, #163029);
                    padding: 6rem 3rem;
                    text-align: center;
                    position: relative;
                    overflow: hidden;
                }

                .hero-section::before, .hero-section::after {
                    content: '';
                    position: absolute;
                    width: 200px;
                    height: 200px;
                    background: radial-gradient(circle at center, rgba(72,187,120,0.3) 0%, transparent 70%);
                    z-index: 0;
                }
                .hero-section::before { top: -50px; left: -50px; transform: rotate(45deg); }
                .hero-section::after { bottom: -50px; right: -50px; transform: rotate(-45deg); }


                .hero-content {
                    max-width: 800px;
                    margin: 0 auto;
                    position: relative;
                    z-index: 1;
                }

                .hero-title {
                    font-size: 3rem;
                    font-weight: 800;
                    color: var(--text-color);
                    margin-bottom: 1.5rem;
                    line-height: 1.2;
                }

                .hero-description {
                    font-size: 1.15rem;
                    color: var(--light-text);
                    margin-bottom: 2.5rem;
                    line-height: 1.6;
                }

                .hero-buttons {
                    display: flex;
                    justify-content: center;
                    gap: 1.5rem;
                }

                .hero-button {
                    padding: 1rem 2.5rem;
                    border: none;
                    border-radius: 8px;
                    font-size: 1.1rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .hero-button.primary {
                    background-color: var(--green-accent);
                    color: var(--dark-bg);
                }
                .hero-button.primary:hover {
                    background-color: var(--primary-button-hover);
                    box-shadow: 0 4px 15px rgba(72,187,120,0.4);
                }

                /* Main Navigation Section Styles */
                .main-navigation-section {
                    padding: 4rem 3rem;
                    text-align: center;
                    background-color: var(--dark-bg);
                }

                .section-title {
                    font-size: 2.2rem;
                    font-weight: 700;
                    color: var(--text-color);
                    margin-bottom: 0.75rem;
                }

                .section-subtitle {
                    font-size: 1.05rem;
                    color: var(--light-text);
                    margin-bottom: 3rem;
                }

                .nav-cards-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
                    gap: 2.5rem;
                    max-width: 1200px;
                    margin: 0 auto;
                }

                .nav-card {
                    background-color: var(--card-bg);
                    border-radius: 12px;
                    padding: 2.5rem;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    text-align: left;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.3);
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                    border: 1px solid var(--border-color);
                }
                .nav-card:hover {
                    transform: translateY(-8px);
                    box-shadow: 0 8px 30px rgba(0,0,0,0.4);
                }

                .card-icon-wrapper {
                    font-size: 3.5rem;
                    color: var(--green-accent);
                    background-color: rgba(72,187,120,0.1);
                    padding: 1rem;
                    border-radius: 50%;
                    margin-bottom: 1.5rem;
                    display: inline-flex;
                }

                .card-title {
                    font-size: 1.6rem;
                    font-weight: 700;
                    color: var(--text-color);
                    margin-bottom: 1rem;
                }

                .card-description {
                    font-size: 0.95rem;
                    color: var(--light-text);
                    line-height: 1.6;
                    margin-bottom: 2rem;
                    flex-grow: 1;
                }

                .card-buttons {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.75rem;
                    margin-top: auto;
                    width: 100%;
                }

                .card-button {
                    padding: 0.7rem 1.2rem;
                    border: none;
                    border-radius: 6px;
                    font-size: 0.9rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    flex-shrink: 0;
                }
                .card-button.primary { background-color: var(--green-accent); color: var(--dark-bg); }
                .card-button.primary:hover { background-color: var(--primary-button-hover); }
                .card-button.secondary { background-color: var(--secondary-button-bg); color: var(--text-color); }
                .card-button.secondary:hover { background-color: var(--secondary-button-hover); }


                /* Footer Styles */
                .dashboard-footer {
                    background-color: #111827;
                    padding: 3rem 2rem;
                    text-align: center;
                    border-top: 1px solid var(--border-color);
                }
                .footer-logo {
                    display: flex; align-items: center; justify-content: center;
                    gap: 0.75rem; margin-bottom: 1rem;
                }
                .footer-logo img { height: 30px; }
                .footer-logo span { font-size: 1.25rem; font-weight: 600; color: var(--text-color); }
                .footer-tagline {
                    max-width: 500px; margin: 0 auto 1.5rem auto; color: var(--light-text);
                    font-size: 0.9rem; line-height: 1.6;
                }
                .footer-social-icons {
                    display: flex; justify-content: center; gap: 1.5rem; margin-bottom: 1.5rem;
                }
                .footer-social-icons a { color: var(--light-text); font-size: 1.5rem; transition: color 0.2s ease-in-out, transform 0.2s ease-in-out; }
                .footer-social-icons a:hover { color: var(--green-accent); transform: translateY(-2px); }
                .footer-copyright {
                    margin-top: 2rem; padding-top: 2rem; border-top: 1px solid var(--border-color);
                    color: var(--light-text); font-size: 0.8rem;
                }

                /* Admin Section Styles */
                 .admin-section {
                    background-color: #111827;
                    border-top: 1px solid var(--border-color);
                    border-bottom: 1px solid var(--border-color);
                 }

                 /* Media Query */
                 @media (max-width: 768px) {
                    /* Header adjustments are handled within Header component's styles now */
                    .hero-section { padding: 4rem 1.5rem; }
                    .hero-title { font-size: 2.2rem; }
                    .hero-description { font-size: 1rem; }
                    .hero-buttons { flex-direction: column; gap: 1rem; }
                    .hero-button { width: 100%; padding: 0.8rem 1.5rem; }
                    .main-navigation-section { padding: 3rem 1.5rem; }
                    .section-title { font-size: 1.8rem; }
                    .section-subtitle { font-size: 0.9rem; }
                    .nav-cards-grid { grid-template-columns: 1fr; gap: 1.5rem; }
                    .nav-card { padding: 1.5rem; align-items: center; text-align: center; }
                    .card-icon-wrapper { margin-bottom: 1rem; font-size: 3rem; }
                    .card-title { font-size: 1.4rem; }
                    .card-buttons { justify-content: center; }
                }
            `}</style>
        </div>
    );
}